import type { Route } from 'vue-router'
import { RouteParamValueT } from '~/mixins/routeQueryMixin'
import { getStoredEntryReferrer } from '~/utils/routeGroups'

export type RouteNavigationParams = Partial<Pick<Route, 'name' | 'params' | 'query'>>

export const QUERY_PARAM_ARRAY_SPLITTER = ','

export function encodeRoute({ name, params, query }: RouteNavigationParams = {}): string {
  if (!name) {
    return ''
  }

  return encodeURIComponent(
    JSON.stringify({
      name,
      params,
      query
    })
  )
}

export function extractRouteParams({
  name,
  params,
  query
}: RouteNavigationParams = {}): RouteNavigationParams | null {
  if (!name) {
    return null
  }

  return { name, params, query }
}

export function getFilterArray(fltrStr: RouteParamValueT, arraySplitter?: string): string[] {
  return fltrStr?.toString().split(arraySplitter ?? QUERY_PARAM_ARRAY_SPLITTER) ?? []
}

type BackRouteConfig =
  | { mode: 'fixed'; name: string; params: string[] }
  | { mode: 'referrer'; group?: string; fallbackName?: string; params: string[] }

const REPO_PARAMS = ['provider', 'owner', 'repo']
const TARGET_PARAMS = ['provider', 'owner', 'repo', 'target']

const BACK_ROUTES: Record<string, BackRouteConfig> = {
  // Run pages — group set, sessionStorage referrer
  'provider-owner-repo-run-runId': {
    mode: 'referrer',
    group: 'run',
    params: REPO_PARAMS
  },
  'provider-owner-repo-run-runId-analyzer': {
    mode: 'referrer',
    group: 'run',
    params: REPO_PARAMS
  },
  'provider-owner-repo-run-runId-sca-targetId': {
    mode: 'referrer',
    group: 'run',
    params: REPO_PARAMS
  },
  'provider-owner-repo-run-runId-sca-targetId-occurrenceId': {
    mode: 'referrer',
    group: 'run',
    params: REPO_PARAMS
  },

  // Dependency target tabs — always go to dependencies list, ignore referrer
  'provider-owner-repo-dependencies-target-vulnerabilities': {
    mode: 'fixed',
    name: 'provider-owner-repo-dependencies',
    params: REPO_PARAMS
  },
  'provider-owner-repo-dependencies-target-packages': {
    mode: 'fixed',
    name: 'provider-owner-repo-dependencies',
    params: REPO_PARAMS
  },
  'provider-owner-repo-dependencies-target-settings': {
    mode: 'fixed',
    name: 'provider-owner-repo-dependencies',
    params: REPO_PARAMS
  },

  // Occurrence page — use referrer if available, fall back to vulnerabilities tab
  'provider-owner-repo-dependencies-target-vulnerabilities-occurrence': {
    mode: 'referrer',
    fallbackName: 'provider-owner-repo-dependencies-target-vulnerabilities',
    params: TARGET_PARAMS
  }
}

function pickParams(routeParams: Route['params'], keys: string[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const key of keys) {
    if (routeParams[key]) result[key] = routeParams[key]
  }
  return result
}

export function getBackRoute(
  currentRoute: Route,
  options: {
    fallbackName?: string
    referrer?: RouteNavigationParams | null
  } = {}
): RouteNavigationParams {
  const routeName = currentRoute.name ?? ''
  const config = BACK_ROUTES[routeName]

  if (!config) {
    return {
      name: 'provider-owner-repo',
      params: pickParams(currentRoute.params, REPO_PARAMS)
    }
  }

  switch (config.mode) {
    case 'fixed':
      return {
        name: config.name,
        params: pickParams(currentRoute.params, config.params)
      }

    case 'referrer': {
      if (config.group) {
        const stored = getStoredEntryReferrer(config.group)
        if (stored?.name) {
          return { name: stored.name, params: stored.params, query: stored.query }
        }
      } else {
        const referrer = options.referrer
        if (referrer?.name && referrer.name !== currentRoute.name) {
          return { name: referrer.name, params: referrer.params, query: referrer.query }
        }
      }
      const fallbackName = config.fallbackName ?? options.fallbackName
      if (fallbackName) {
        return {
          name: fallbackName,
          params: pickParams(currentRoute.params, config.params)
        }
      }
      return {
        name: 'provider-owner-repo',
        params: pickParams(currentRoute.params, REPO_PARAMS)
      }
    }
  }
}
